// PlanesYPago.jsx
//
// El bloque compartido del mockup elegido (#1680, mezcla C + A):
// `tools/mockups/smartdx_mockups_pago_chile_internacional.html`.
//   - A: dentro de un diálogo (tarjeta de prueba, "Mi suscripción").
//   - C: columna derecha de la pantalla de bloqueo (prueba o pago vencido).
// Un solo componente para los dos: elegir plan, país, casilla de términos (solo
// Paddle), pagar y resultado. Cubre R26, R27, R28, R29, R30, R31, R36.
//
// Chile paga con Flow (CLP, redirección a flow.cl); fuera de Chile con Paddle
// (USD, overlay). El riel lo decide el backend (`GET /api/billing/me/checkout/`),
// acá no se decide nada de plata.
// Cubre R10 — .claude/specs/1809-docs-pago/
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import useCheckout, { sondearAcceso } from "../../hooks/useCheckout";
import { abrirCheckoutPaddle } from "../../helpers/paddle";
import { formatoPrecio, nombrePlan as nombreDePlan, PLAN_NOMBRE } from "../../helpers/checkout-formato";
import { PAISES, paisPorIso } from "../../helpers/phone";
import { SOPORTE } from "../../helpers/soporte";
import { useCurrentUser } from "../context/UserContext";
import getClient from "../../helpers/get-client";
import { documentosDelCheckout } from "../../helpers/documentos-pago";
// (#1809 documentos) importes antes de botonesSx: la rama jornada-def agrega el suyo justo después y así no chocan.
import PasoDocumentos, { DocumentosEnviados, PasosPago } from "./PasoDocumentos";
import { botonGhostSx, botonPrimarioSx } from "./botonesSx";
import JornadasAlMes, { hayExcepcionJornadas, sinTopeJornadas } from "./JornadasAlMes";
// #1887 · «No quiero un plan: jornadas por separado» al final de la lista (R33) y la compra en este mismo bloque.
import { useOfertaSuelta } from "../../hooks/useJornadaEstado";
import OpcionSinPlan from "../suelta/OpcionSinPlan";
import CompraSuelta from "../suelta/CompraSuelta";

const RIEL_NOMBRE = { flow: "Flow", paddle: "Paddle", transferencia: "transferencia" };
const CORREO_PAGOS = "pagos@smardx.com";

// "con Flow" / "con Paddle" / "por transferencia"
const conRiel = (riel) => (riel === "transferencia" ? "por transferencia" : `con ${RIEL_NOMBRE[riel]}`);

const JORNADAS_SIN_TOPE = "Jornadas sin tope";

// #1887: «3 jornadas al mes · o 6 de 12 h» (mockup `jornada_suelta_pago.pdf`, pantalla 1): cada jornada del plan vale dos de 12 h.
const textoJornadas = (p) =>
  p.plan === "max_plus" || p.jornadas == null
    ? JORNADAS_SIN_TOPE
    : `${p.jornadas} jornadas al mes · o ${p.jornadas * 2} de 12\u00a0h`;

// Definición de jornada que se lee al suscribirse (#1809, Sebastián 28-oct). Mismas palabras que la home
// (`home_v4.html`, «.jdef») y que la orden de contratación (`billing/orden.py`, DEF_JORNADA). Desde #1887 (R42) la definición
// la manda el backend en `GET /me/checkout/` (`definicion_jornada`, una sola constante del servidor para la orden de los planes y
// esta pantalla: «máximo 25 horas» y la jornada de 12 h que se activa con un botón; la orden de la suelta NO la usa, lleva su
// frase propia, R32 v8 P26). Este texto es SOLO el respaldo
// para un backend que todavía no manda la clave (ventana de un deploy), y repite la del servidor. «Subir de plan» no aplica a Max+
// («Jornadas sin tope»).
// Cubre R42 — .claude/specs/jornada-suelta/
const DEF_JORNADA =
  "Una jornada de 24 h parte con tu primer documento y dura hasta las 10:00 de la mañana siguiente (máximo 25 horas). Si te basta con una jornada de 12 h, la activas con el botón y dura 13 horas desde que la confirmas. Dentro de una jornada generas los documentos que necesites.";
const DEF_JORNADA_TOPE = " Si usas todas tus jornadas del mes, para seguir puedes subir de plan.";

// El nodo `checkout-nota-jornada` lleva SOLO la definición (R42: «dice exactamente» lo que manda el backend); la frase del tope va aparte.
const NotaJornada = ({ conTope = true, definicion = "" }) => (
  <Typography component="div" sx={{ fontSize: 12, color: "var(--text-muted)", mt: 1 }}>
    <span data-testid="checkout-nota-jornada">{definicion || DEF_JORNADA}</span>
    {conTope ? <span data-testid="checkout-nota-jornada-tope">{DEF_JORNADA_TOPE}</span> : null}
  </Typography>
);

const notaImpuestos = (estado, soloTransferencia = false) => {
  const cohorte = estado.cohorte === "fundador" ? "Precio fundador" : "Precio de lista";
  if (soloTransferencia) return `${cohorte} en dólares. Es el monto final a transferir.`;
  return estado.moneda === "USD"
    ? `${cohorte} en dólares. Los impuestos de tu país, si aplican, se calculan al pagar.`
    : `${cohorte} en pesos chilenos. IVA incluido.`;
};

const lineaPais = (estado) => {
  const nombre = paisPorIso(estado.pais).nombre;
  if (estado.moneda === "USD") return `Facturación en ${nombre}, dólares (USD).`;
  if (estado.moneda === "CLP") return "Facturación en Chile, pesos chilenos (CLP).";
  return `Facturación en ${nombre}.`;
};

const textoNoHabilitado = (motivo) =>
  motivo === "anexo_borrador"
    ? `El pago desde tu país todavía no está abierto. Escríbenos a ${SOPORTE} y activamos tu plan a mano.`
    : `El pago en línea todavía no está activo. Escríbenos a ${SOPORTE} y activamos tu plan a mano.`;

const mensajeDeError = (code) => {
  switch (code) {
    case "ya_suscrito":
      return "Ya tienes una suscripción activa. Si no ves tu acceso, vuelve a cargar la página.";
    case "terminos_requeridos":
      return "Marca la casilla de los términos para continuar.";
    case "riel_no_habilitado":
    case "transferencia_no_disponible":
      return `El pago desde tu país todavía no está abierto. Escríbenos a ${SOPORTE} y activamos tu plan a mano.`;
    case "pais_sin_cobro":
      return `Por ahora no tenemos suscripción en tu país. Escríbenos a ${SOPORTE} y vemos cómo ayudarte.`;
    case "cancelacion_programada":
      return `Tienes la cancelación de tu plan programada al fin del período. Cuando termine puedes volver a suscribirte; si quieres seguir con tarjeta antes, escríbenos a ${SOPORTE}.`;
    case "correo_invalido":
      return `Flow no aceptó tu correo. Revisa tu correo en tu perfil y vuelve a intentarlo, o escríbenos a ${SOPORTE}.`;
    case "cobro_anterior_vivo":
      return `Tu cobro anterior todavía no figura cerrado en Flow. Escríbenos a ${SOPORTE} y lo dejamos listo.`;
    case "primero_transferencia":
      return "Tu período venció: primero ponlo al día por transferencia. Después puedes dejar tu tarjeta para los períodos siguientes.";
    case "documentos_requeridos":
      return "Para continuar necesitamos tus documentos. Súbelos y sigues con tu suscripción.";
    case "orden_requerida":
      return "Marca la casilla de la orden de contratación para continuar.";
    case "orden_cambio":
      return "Las condiciones de tu plan cambiaron recién. Revísalas y vuelve a aceptarlas.";
    case "precio_no_calza":
      return `El precio de tu plan cambió y no podemos cobrarlo desde aquí. Escríbenos a ${SOPORTE} y lo resolvemos hoy.`;
    default:
      return `No pudimos iniciar el pago. Intenta de nuevo o escríbenos a ${SOPORTE}.`;
  }
};

// Códigos 409 tras los cuales el GET puede haber cambiado (candado, lista roja):
// se vuelve a pedir para mostrar lo que hoy rige, con el mensaje a la vista.
const RECARGAR_CODES = new Set(["pais_sin_cobro", "riel_no_habilitado", "transferencia_no_disponible", "orden_cambio", "documentos_requeridos"]);

// #1809 (decisión de Sebastián 27-oct): la «orden de contratación aceptada dentro de la app» que nombran
// los Términos (§5). El TEXTO lo arma el backend (`billing/orden.py`) con el plan, el precio, las
// jornadas y el día real del cobro; acá solo se muestra y se manda su hash. El back lo recalcula y, si
// no calza, no cobra (409 `orden_cambio`): lo que el médico acepta es exactamente lo que se cobra.
const OrdenContratacion = ({ orden, acepta, onCambio }) => (
  <Box
    component="label"
    data-testid="checkout-orden"
    sx={{
      display: "flex",
      gap: 1.1,
      alignItems: "flex-start",
      fontSize: 12.3,
      lineHeight: 1.5,
      color: "var(--text-tertiary)",
      mb: 1.5,
      p: "10px 12px",
      borderRadius: "12px",
      backgroundColor: "var(--bg-input)",
      border: "1px solid var(--border-soft)",
      cursor: "pointer",
      "& a": { color: "var(--color-info)" },
    }}
  >
    <Box
      component="input"
      type="checkbox"
      data-testid="checkout-orden-casilla"
      checked={acepta}
      onChange={(e) => onCambio(e.target.checked)}
      sx={{ mt: "3px", accentColor: "var(--color-primary)", flex: "0 0 auto" }}
    />
    <span>
      <span data-testid="checkout-orden-texto">{orden.texto}</span>{" "}
      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
        Ver Términos de Uso
      </a>
    </span>
  </Box>
);

const Fine = ({ children, ...rest }) => (
  <Typography
    {...rest}
    sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: 1.25, lineHeight: 1.5 }}
  >
    {children}
  </Typography>
);

const FilaPlan = ({ plan, moneda, fundador, activo, onElegir }) => {
  const conLista = fundador && Number(plan.precio_lista) > Number(plan.precio);
  return (
    <Box
      role="radio"
      aria-checked={activo}
      tabIndex={0}
      data-testid={`checkout-plan-${plan.plan}`}
      onClick={onElegir}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onElegir();
        }
      }}
      sx={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0,1fr) auto",
        gap: 1.5,
        alignItems: "center",
        p: "12px 14px",
        borderRadius: "13px",
        cursor: "pointer",
        backgroundColor: activo ? "rgba(var(--color-primary-rgb), 0.12)" : "var(--bg-input)",
        border: `1px solid ${activo ? "var(--color-primary)" : "transparent"}`,
        "&:hover": { borderColor: activo ? "var(--color-primary)" : "var(--border-strong)" },
        "&:focus-visible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: `2px solid ${activo ? "var(--color-primary)" : "var(--border-strong)"}`,
          display: "grid",
          placeItems: "center",
          "&::after": {
            content: '""',
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "var(--color-primary)",
            transform: activo ? "scale(1)" : "scale(0)",
            transition: "transform 160ms",
          },
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
          {nombreDePlan(plan.plan)}
          {plan.plan === "pro" && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.03em",
                px: "7px",
                py: "2px",
                borderRadius: "999px",
                verticalAlign: "1px",
                color: "var(--color-info)",
                backgroundColor: "rgba(var(--color-info-rgb), 0.16)",
                border: "1px solid rgba(var(--color-info-rgb), 0.3)",
              }}
            >
              Recomendado
            </Box>
          )}
        </Box>
        <Box sx={{ fontSize: 12, color: "var(--text-muted)" }}>{textoJornadas(plan)}</Box>
      </Box>
      <Box sx={{ textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        <Box
          data-testid={`checkout-precio-${plan.plan}`}
          sx={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}
        >
          {formatoPrecio(plan.precio, moneda)}
        </Box>
        <Box sx={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 500 }}>
          {conLista && (
            <Box
              component="span"
              data-testid={`checkout-lista-${plan.plan}`}
              sx={{ textDecoration: "line-through", mr: 0.75 }}
            >
              {formatoPrecio(plan.precio_lista, moneda)}
            </Box>
          )}
          al mes
        </Box>
      </Box>
    </Box>
  );
};

const SelectorPais = ({ actual, onElegir }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, maxHeight: 168, overflowY: "auto" }}>
    {PAISES.map((p) => {
      const on = p.iso === actual;
      return (
        <Box
          key={p.iso}
          component="button"
          type="button"
          data-testid={`checkout-pais-${p.iso}`}
          aria-pressed={on}
          onClick={() => onElegir(p.iso)}
          sx={{
            font: "inherit",
            fontSize: 12,
            px: "11px",
            py: "4px",
            borderRadius: "999px",
            cursor: "pointer",
            border: `1px solid ${on ? "var(--color-primary)" : "var(--border-strong)"}`,
            backgroundColor: on ? "var(--color-primary)" : "transparent",
            color: on ? "var(--text-on-primary)" : "var(--text-tertiary)",
          }}
        >
          {p.nombre}
        </Box>
      );
    })}
  </Box>
);

const copiar = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    /* sin permiso de portapapeles: el dato igual está a la vista para copiarlo a mano */
  }
};

const FilaDato = ({ etiqueta, valor, testid }) => (
  <Box
    data-testid={testid}
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "2px 12px",
      py: "5px",
      borderTop: "1px solid var(--border-soft)",
      fontSize: 12.6,
    }}
  >
    <Box component="span" sx={{ color: "var(--text-muted)" }}>
      {etiqueta}
    </Box>
    <Box
      component="span"
      sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.75, fontVariantNumeric: "tabular-nums", color: "var(--text-primary)" }}
    >
      {valor}
      <Box
        component="button"
        type="button"
        aria-label={`Copiar ${etiqueta}`}
        data-testid="checkout-transferencia-copiar"
        onClick={() => copiar(String(valor))}
        sx={{ border: 0, background: "transparent", color: "var(--text-muted)", cursor: "pointer", p: "2px", display: "inline-flex" }}
      >
        <ContentCopyIcon sx={{ fontSize: 13 }} />
      </Box>
    </Box>
  </Box>
);

const CajaTransferencia = ({
  transferencia,
  monto,
  principal,
  deshabilitado,
  avisando,
  onAvisar,
  avisada = false,
  onRevisar,
  mensajeRevision = false,
  revisando = false,
}) => (
  <Box
    data-testid="checkout-transferencia"
    sx={{
      display: "grid",
      gap: 1,
      p: "12px 14px",
      borderRadius: "13px",
      backgroundColor: "var(--bg-input)",
      border: "1px solid var(--border-soft)",
    }}
  >
    <Box sx={{ fontWeight: 700, fontSize: 13.5, color: "var(--text-primary)" }}>Transferencia internacional</Box>
    <Box>
      {(transferencia.datos || []).map((d) => (
        <FilaDato key={d.etiqueta} etiqueta={d.etiqueta} valor={d.valor} />
      ))}
      <FilaDato etiqueta="Monto" valor={monto} testid="checkout-transferencia-monto" />
    </Box>
    {transferencia.nota && (
      <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", lineHeight: 1.5 }}>{transferencia.nota}</Typography>
    )}
    <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", lineHeight: 1.5 }}>
      Manda el comprobante a {CORREO_PAGOS}. Activamos tu plan apenas veamos el pago, normalmente el mismo día hábil.
    </Typography>
    {avisada ? (
      // Tras "Ya transferí, avisar" en el bloqueo (como el mockup): los datos bancarios siguen a la vista,
      // el aviso aparece debajo y queda UN solo botón para volver a revisar.
      <>
        <Box
          data-testid="checkout-pendiente"
          sx={{
            p: "10px 12px",
            borderRadius: "12px",
            backgroundColor: "rgba(var(--color-warning-rgb), 0.08)",
            border: "1px solid rgba(var(--color-warning-rgb), 0.3)",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
            Recibimos tu aviso de pago
          </Typography>
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", mt: 0.25, lineHeight: 1.5 }}>
            Cuando registremos tu pago se abre tu acceso. Puedes volver a revisar aquí.
          </Typography>
          {mensajeRevision && !revisando && (
            <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: 0.75, lineHeight: 1.5 }}>
              {`Todavía no registramos tu transferencia. Normalmente es el mismo día hábil. Si pasa más de un día, escríbenos a ${SOPORTE}.`}
            </Typography>
          )}
        </Box>
        <Button
          data-testid="checkout-revisar"
          variant="outlined"
          fullWidth
          disabled={revisando}
          onClick={onRevisar}
          sx={botonGhostSx}
        >
          {revisando ? "Revisando..." : "Volver a revisar"}
        </Button>
      </>
    ) : (
    <Button
      data-testid="checkout-transferencia-avisar"
      variant={principal ? "contained" : "outlined"}
      disableElevation
      fullWidth
      disabled={deshabilitado || avisando}
      onClick={onAvisar}
      sx={principal ? botonPrimarioSx : botonGhostSx}
    >
      {avisando ? "Avisando..." : "Ya transferí, avisar"}
    </Button>
    )}
  </Box>
);

// "Actualizar tarjeta" de una suscripción Paddle vencida (I4): el portal vive en
// otro dominio y la URL la da el backend. La pestaña se abre ANTES de la espera
// (Safari bloquea window.open tras un await), igual que en Mi suscripción.
const BotonActualizarTarjeta = () => {
  const [abriendo, setAbriendo] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const abrir = async () => {
    setAbriendo(true);
    setMensaje("");
    const pestana = window.open("", "_blank");
    try {
      const res = await getClient().get("/api/billing/me/portal/");
      const url = res?.data?.url;
      if (!url) throw new Error("sin_url");
      if (pestana) {
        pestana.opener = null;
        pestana.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch {
      if (pestana) pestana.close();
      setMensaje(`No pudimos abrir el portal de pagos. Intenta de nuevo o escríbenos a ${SOPORTE}.`);
    } finally {
      setAbriendo(false);
    }
  };
  return (
    <>
      <Button
        data-testid="checkout-vencido-actualizar-tarjeta"
        variant="contained"
        disableElevation
        fullWidth
        disabled={abriendo}
        onClick={abrir}
        sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14 }}
      >
        {abriendo ? "Abriendo..." : "Actualizar tarjeta"}
      </Button>
      {mensaje ? (
        <Typography
          data-testid="checkout-vencido-mensaje"
          role="alert"
          sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1 }}
        >
          {mensaje}
        </Typography>
      ) : (
        <Fine>Se abre el portal de pagos en una pestaña nueva. Al actualizar la tarjeta se reintenta el cobro.</Fine>
      )}
    </>
  );
};

const Resultado = ({ icono, titulo, lead, fine, children, ...rest }) => (
  <Box sx={{ textAlign: "center", py: 1.5 }} {...rest}>
    {icono}
    <Typography sx={{ mt: 1.5, mb: 0.75, fontSize: 21, fontWeight: 800, lineHeight: 1.25, color: "var(--text-primary)" }}>
      {titulo}
    </Typography>
    {lead && (
      <Typography sx={{ fontSize: 13.2, color: "var(--text-muted)", mb: 1 }}>{lead}</Typography>
    )}
    {fine && <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)" }}>{fine}</Typography>}
    <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>{children}</Box>
  </Box>
);

const PlanesYPago = ({
  titulo = "Elige tu plan",
  onListo,
  onVolver,
  onFase,
  onOfreceSuelta,
  faseInicial = "planes",
  rielInicial = "flow",
  abajo = null,
  // Pantalla de bloqueo: el acceso NO sigue abierto mientras se confirma un pago.
  bloqueado = false,
  // Pago vencido (I4): con `suscripcion` en el GET no se ofrece elegir plan.
  pagoVencido = false,
  // #1809 (cobro programado): el médico ya tiene plan a mano y se le pide registrar la tarjeta.
  // Misma rama "Tu plan" que el pago vencido, pero con el botón de Flow: el backend reutiliza su
  // `activa` manual y Flow cobra desde `period_end + 1` (flow_service._crear_suscripcion_en_flow).
  modoAviso = false,
  // Se llama antes de salir a Flow (el aviso cuenta el click para el monitoreo del admin).
  onPagar,
  // #1809: texto del botón de pago cuando no hay lista de planes ("Registrar tarjeta con Flow").
  etiquetaPagarSus = "",
}) => {
  const { currentUser, refreshUser } = useCurrentUser();
  const { estado, cargando, error, cargar, iniciar } = useCheckout();
  // #1887 (R33): la oferta de jornadas sueltas de ESTA cuenta; el backend dice si se ofrece (no en la prueba, no a sin tope).
  // `modo` = "suelta" cambia la lista de planes por la compra de jornadas sueltas, dentro del mismo bloque.
  const { oferta } = useOfertaSuelta();
  const [modo, setModo] = useState("planes");
  const [plan, setPlan] = useState("pro");
  const [acepta, setAcepta] = useState(false);
  const [aceptaOrden, setAceptaOrden] = useState(false);
  const [eligiendoPais, setEligiendoPais] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [errorPago, setErrorPago] = useState("");
  // planes | confirmando | listo | pendiente
  const [fase, setFase] = useState(faseInicial);
  const [rielResultado, setRielResultado] = useState(rielInicial);
  const [mensajeRevision, setMensajeRevision] = useState(false);
  const [trAbierta, setTrAbierta] = useState(false);
  // #1809 · orden «Tus documentos · Suscripción» (Sebastián, 31-oct-2026: primero los papeles, después elegir la
  // suscripción), en esta misma ventana. `docsEnviados` = los subió en ESTE bloque: el GET que se recarga ya
  // trae `exige: false`, pero mientras llega no se vuelve a mostrar el formulario.
  const [docsEnviados, setDocsEnviados] = useState(false);

  // El que aloja este bloque necesita saber cuándo hay un pago en curso: la
  // pantalla de bloqueo se retiene hasta que el médico vea el resultado.
  const onFaseRef = useRef(onFase);
  onFaseRef.current = onFase;
  // Con la transferencia avisada en el bloqueo la caja trae su propio botón de revisar: se avisa
  // para que la pantalla de bloqueo no sume un segundo.
  const botonPropio = rielResultado === "transferencia" && bloqueado && Boolean(estado?.transferencia);
  useEffect(() => {
    if (onFaseRef.current) onFaseRef.current(fase, { botonPropio });
  }, [fase, botonPropio]);

  // Si el plan por defecto ("pro") no está en la lista, se parte del primero.
  const planes = useMemo(() => estado?.planes || [], [estado]);
  useEffect(() => {
    if (planes.length && !planes.some((p) => p.plan === plan)) setPlan(planes[0].plan);
  }, [planes, plan]);

  // R31: cada 3 s hasta 60 s. Se reinicia al volver a "confirmando" (botón
  // "Volver a revisar").
  useEffect(() => {
    if (fase !== "confirmando") return undefined;
    let cancelado = false;
    sondearAcceso(refreshUser, { cancelado: () => cancelado }).then((r) => {
      if (cancelado || r === "cancelado") return;
      setFase(r === "listo" ? "listo" : "pendiente");
    });
    return () => {
      cancelado = true;
    };
  }, [fase, refreshUser]);

  const riel = estado?.riel;
  const esPaddle = riel === "paddle";
  const planActual = planes.find((p) => p.plan === plan);
  // I4: pago vencido con suscripción conocida por el backend. El plan y el precio
  // son los de ELLA, nunca los de la lista (un solo monto en pantalla).
  // Una `cancelada` (vencida también cae acá) no tiene un cobro vivo que actualizar: el backend deja
  // contratar de nuevo y reutiliza su fila, así que ve la lista de planes y paga como cuenta nueva.
  const sus =
    (pagoVencido || modoAviso) && estado?.suscripcion && estado.suscripcion.status !== "cancelada"
      ? estado.suscripcion
      : null;
  const planAPagar = sus ? sus.plan : plan;
  // #1887 R33: con la lista de planes a la vista (no el pago vencido de una suscripción viva) y solo en el riel de Flow.
  const ofreceSuelta = Boolean(oferta?.disponible) && !sus && riel === "flow";
  // P25 (#1887): el bloqueo por prueba terminada nombra las jornadas adicionales en su bajada SOLO si esta pantalla las
  // ofrece de verdad. Se le informa el valor ya resuelto (oferta Y riel Flow Y sin suscripción viva). Cambiar el país a
  // CO/PE lo apaga y la bajada vuelve al texto de siempre. Si el checkout nunca cargó (cargando, o falló la primera
  // carga) tampoco hay riel y va el texto de siempre. Si falla una RECARGA, la pantalla se queda con el país anterior y
  // la bajada lo acompaña (no vuelve al texto de siempre). Al desmontar avisa `false`.
  // Cubre R33 — .claude/specs/jornada-suelta/
  const onOfreceSueltaRef = useRef(onOfreceSuelta);
  onOfreceSueltaRef.current = onOfreceSuelta;
  useEffect(() => {
    if (onOfreceSueltaRef.current) onOfreceSueltaRef.current(ofreceSuelta);
    return () => {
      if (onOfreceSueltaRef.current) onOfreceSueltaRef.current(false);
    };
  }, [ofreceSuelta]);
  // #1809: el back dice si a esta cuenta se le exigen los documentos para pagar (y el POST los exige igual).
  const docs = documentosDelCheckout(estado);
  const pideDocs = docs.exige && !docsEnviados;
  const docsEnRevision = docsEnviados || docs.estado === "enviados";
  // #1887 F5 · `docsEnviados` es un puente hasta que llega el GET; el estado real manda. Si durante la sesión los
  // documentos pasan a «rechazados» (el admin los devolvió), la bandera local se baja: si no, «Documentos enviados.»
  // quedaba a la vista y `CompraSuelta` (vía `docsYaEnviados`) se saltaba el paso de documentos. Se mira el CAMBIO
  // hacia rechazados y no el valor: reenviar tras un rechazo parte ya en «rechazados» y la bandera debe sostenerse
  // hasta que el GET recargado traiga «enviados».
  const estadoDocsPrevio = useRef(docs.estado);
  useEffect(() => {
    if (docs.estado === "rechazados" && estadoDocsPrevio.current !== "rechazados") setDocsEnviados(false);
    estadoDocsPrevio.current = docs.estado;
  }, [docs.estado]);
  // Paso del indicador: 1 documentos · 2 suscripción · 0 = sin indicador (a esta cuenta no se le piden
  // papeles: Hans exento, fuera de Chile, ya enviados al abrir). Sin documentos no hay un paso previo que
  // explicar: el bloque queda como siempre.
  const flujoDocs = Boolean(estado) && riel !== "no_disponible" && (docs.exige || docsEnviados);
  const paso = !flujoDocs ? 0 : pideDocs ? 1 : 2;
  // #1809: la orden que corresponde a lo que se va a pagar (solo Flow; el back la manda si aplica).
  const orden = riel === "flow" ? (sus ? estado?.orden_suscripcion : estado?.ordenes?.[plan]) || null : null;
  const faltaOrden = Boolean(orden) && !aceptaOrden;
  // Cambiar de plan cambia el texto: la aceptación anterior no vale para el nuevo.
  useEffect(() => {
    setAceptaOrden(false);
  }, [orden?.hash]);

  const cambiarPais = (iso) => {
    setEligiendoPais(false);
    setAcepta(false);
    setErrorPago("");
    cargar(iso);
  };

  const pagar = async () => {
    if ((!planActual && !sus) || pagando) return;
    setPagando(true);
    setErrorPago("");
    if (onPagar) onPagar();
    const r = await iniciar({
      plan: planAPagar,
      pais: estado.pais,
      aceptaTerminos: esPaddle ? acepta : false,
      ordenHash: orden && aceptaOrden ? orden.hash : undefined,
    });
    if (!r.ok) {
      setPagando(false);
      setErrorPago(mensajeDeError(r.code));
      // El admin los rechazó entre tanto: el formulario vuelve aunque este bloque los haya enviado antes.
      if (r.code === "documentos_requeridos") {
        setDocsEnviados(false);
      }
      if (RECARGAR_CODES.has(r.code)) cargar(estado.pais);
      return;
    }
    if (r.data.riel === "flow") {
      // R30: la inscripción de la tarjeta vive en flow.cl; vuelve a /perfil?pago=flow.
      window.location.assign(r.data.url_registro);
      return;
    }
    try {
      const resultado = await abrirCheckoutPaddle({
        config: estado.paddle || {},
        priceId: r.data.price_id,
        email: r.data.email,
        customData: r.data.custom_data,
      });
      setPagando(false);
      if (resultado === "completado") {
        setRielResultado("paddle");
        setMensajeRevision(false);
        setFase("confirmando");
      }
    } catch {
      setPagando(false);
      setErrorPago(
        `No se pudo abrir el pago. Revisa tu conexión e intenta de nuevo, o escríbenos a ${SOPORTE}.`
      );
    }
  };

  // R42: "Ya transferí, avisar" crea la suscripción pendiente; el acceso lo libera el
  // admin al registrar el pago, así que la pantalla queda en "Pendiente".
  const avisarTransferencia = async () => {
    if (!planActual || pagando) return;
    setPagando(true);
    setErrorPago("");
    const r = await iniciar({ plan, pais: estado.pais, aceptaTerminos: acepta, metodo: "transferencia" });
    setPagando(false);
    if (!r.ok) {
      setErrorPago(mensajeDeError(r.code));
      if (RECARGAR_CODES.has(r.code)) cargar(estado.pais);
      return;
    }
    setRielResultado("transferencia");
    setMensajeRevision(false);
    setFase("pendiente");
  };

  // ── Resultado ──────────────────────────────────────────────────────────
  // Transferencia avisada en el bloqueo: no hay pantalla aparte, la caja con los datos sigue a la
  // vista TODO el tiempo, también mientras se sondea (el sondeo solo cambia el botón, O-G2).
  const avisadaEnBloqueo =
    (fase === "pendiente" || fase === "confirmando") &&
    rielResultado === "transferencia" &&
    bloqueado &&
    Boolean(estado?.transferencia);
  if (fase === "confirmando" && !avisadaEnBloqueo) {
    return (
      <Box data-testid="checkout">
        <Resultado
          data-testid="checkout-confirmando"
          icono={<AutorenewIcon sx={{ fontSize: 56, color: "var(--color-primary-soft)" }} />}
          titulo={`Estamos confirmando tu pago ${conRiel(rielResultado)}`}
          lead={
            rielResultado === "transferencia"
              ? "Revisamos si ya registramos tu transferencia."
              : "Esto toma unos segundos. No se cobra dos veces."
          }
        />
      </Box>
    );
  }
  if (fase === "listo") {
    const planPagado =
      PLAN_NOMBRE[currentUser?.suscripcion?.plan] || PLAN_NOMBRE[plan] || "";
    const extranjero = rielResultado === "paddle";
    return (
      <Box data-testid="checkout">
        <Resultado
          data-testid="checkout-listo"
          icono={<CheckCircleOutlineIcon sx={{ fontSize: 56, color: "var(--color-success)" }} />}
          titulo={planPagado ? `Listo, tu plan ${planPagado} está activo` : "Listo, tu plan está activo"}
          lead="Ya puedes seguir trabajando."
          fine={
            rielResultado === "transferencia"
              ? "Registramos tu transferencia."
              : extranjero
                ? "Paddle te envía el recibo a tu correo."
                // Decisión pendiente de Sebastián (revisor r3 O-F): hoy la boleta se emite y se
                // envía a mano, así que el copy promete lo que se hace ("Te enviamos tu boleta
                // por correo.") en vez de pedirle al médico que la solicite.
                : "Te enviamos tu boleta por correo."
          }
        >
          <Button
            data-testid="checkout-seguir"
            variant="contained"
            disableElevation
            onClick={onListo}
            sx={botonPrimarioSx}
          >
            Seguir trabajando
          </Button>
        </Resultado>
      </Box>
    );
  }
  if (fase === "pendiente" && !avisadaEnBloqueo) {
    return (
      <Box data-testid="checkout">
        <Resultado
          data-testid="checkout-pendiente"
          icono={<AutorenewIcon sx={{ fontSize: 56, color: "var(--color-warning)" }} />}
          titulo={
            rielResultado === "transferencia"
              ? "Recibimos tu aviso de pago"
              : `Estamos confirmando tu pago con ${RIEL_NOMBRE[rielResultado]}`
          }
          lead={
            rielResultado === "transferencia"
              ? bloqueado
                ? `Cuando registremos tu pago se abre tu acceso. Puedes volver a revisar aquí. Recuerda mandar el comprobante a ${CORREO_PAGOS}.`
                : `Sigues en tu prueba mientras tanto. Recuerda mandar el comprobante a ${CORREO_PAGOS}.`
              : "Esto toma unos segundos. No se cobra dos veces."
          }
          fine={
            mensajeRevision
              ? rielResultado === "transferencia"
                ? `Todavía no registramos tu transferencia. Normalmente es el mismo día hábil. Si pasa más de un día, escríbenos a ${SOPORTE}.`
                : `Todavía no llega la confirmación de ${RIEL_NOMBRE[rielResultado]}. Si ya pagaste, dale unos minutos. Si pasa más de una hora, escríbenos a ${SOPORTE}.`
              : undefined
          }
        >
          <Button
            data-testid="checkout-revisar"
            variant="outlined"
            onClick={() => {
              setMensajeRevision(true);
              setFase("confirmando");
            }}
            sx={botonGhostSx}
          >
            Volver a revisar
          </Button>
        </Resultado>
      </Box>
    );
  }

  // ── #1887 · compra de jornadas sueltas (tras «No quiero un plan») ───────────────────────────────────────
  // Compra de jornadas adicionales: `CompraSuelta` (mockup `jornada_suelta_pago.pdf`). Orden vigente: documentos primero
  // si se exigen (nunca se llega aquí sin enviarlos, salvo que los rechacen en la sesión), luego elegir tipo y cantidad,
  // y pago único. Los documentos son los de este mismo checkout (`estado.documentos`); al subirlos se recarga el GET.
  // Cubre R33, R34, R73 — .claude/specs/jornada-suelta/
  if (modo === "suelta" && oferta?.disponible && estado) {
    return (
      <Box data-testid="checkout">
        <CompraSuelta
          oferta={oferta}
          estadoCheckout={estado}
          onRecargarCheckout={() => cargar(estado.pais)}
          onVolver={() => setModo("planes")}
          docsYaEnviados={docsEnviados}
        />
      </Box>
    );
  }

  // ── Planes ─────────────────────────────────────────────────────────────
  const volver = onVolver && (
    <Box
      component="button"
      type="button"
      data-testid="checkout-volver"
      onClick={onVolver}
      sx={{ ...enlaceSx, mt: 1.5 }}
    >
      Volver
    </Box>
  );

  let cuerpo;
  if (cargando && !estado) {
    cuerpo = (
      <Typography data-testid="checkout-cargando" sx={{ fontSize: 13, color: "var(--text-muted)", py: 2 }}>
        Cargando los planes...
      </Typography>
    );
  } else if (error && !estado) {
    cuerpo = (
      <Box data-testid="checkout-error" sx={{ py: 1 }}>
        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 1 }}>
          No pudimos cargar los planes. Revisa tu conexión e intenta de nuevo, o escríbenos a {SOPORTE}.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => cargar()}
          sx={botonGhostSx}
        >
          Reintentar
        </Button>
      </Box>
    );
  } else if (avisadaEnBloqueo) {
    cuerpo = (
      <CajaTransferencia
        transferencia={estado.transferencia}
        monto={formatoPrecio(estado.transferencia.montos?.[plan] ?? planActual?.precio, "USD")}
        principal
        avisada
        revisando={fase === "confirmando"}
        mensajeRevision={mensajeRevision}
        onRevisar={() => {
          setMensajeRevision(true);
          setFase("confirmando");
        }}
      />
    );
  } else if (paso === 1) {
    // #1809 R10: paso 1 de 2, ANTES de elegir la suscripción. Solo cuando el plan ya viene fijado (aviso de
    // cobro, período vencido) se muestra en una línea; con la lista de planes todavía no hay plan elegido.
    const resumen = sus
      ? ["Tu suscripción", PLAN_NOMBRE[sus.plan] && `Plan ${PLAN_NOMBRE[sus.plan]}`, formatoPrecio(sus.precio_bruto, sus.moneda) && `${formatoPrecio(sus.precio_bruto, sus.moneda)} al mes`]
          .filter(Boolean)
          .join(" · ")
      : "";
    cuerpo = (
      <PasoDocumentos
        documentos={docs}
        resumen={resumen}
        onEnviados={() => {
          setDocsEnviados(true);
          cargar(estado.pais);
        }}
      />
    );

  } else if (sus) {
    // I4: pago vencido. "Tu plan: <Plan> · <precio> al mes" y UN camino según el
    // gateway de su suscripción; la lista de planes no se ofrece (el backend cobra
    // el plan de la suscripción, no el que se elija).
    const nombrePlan = PLAN_NOMBRE[sus.plan] || "";
    // N1 (revisor r2): el backend publica `precio_bruto`. Sin precio utilizable no se muestra
    // ningún monto (nunca NaN ni vacío): "Tu plan: Pro" y "Pagar con Flow".
    const precioSus = formatoPrecio(sus.precio_bruto, sus.moneda);
    const flujoManual = sus.gateway === "manual" && sus.moneda === "CLP";
    const mensajeContacto = (texto) => (
      <Typography data-testid="checkout-vencido-contacto" sx={{ fontSize: 13, color: "var(--text-secondary)", mt: 0.5 }}>
        {texto}{" "}
        <Box component="a" href={`mailto:${SOPORTE}`} sx={{ color: "var(--color-info)" }}>
          {SOPORTE}
        </Box>
        .
      </Typography>
    );
    cuerpo = (
      <>
        <Box
          data-testid="checkout-vencido-plan"
          sx={{
            p: "12px 14px",
            borderRadius: "13px",
            backgroundColor: "var(--bg-input)",
            border: "1px solid var(--border-soft)",
            fontWeight: 700,
            fontSize: 14,
            color: "var(--text-primary)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {precioSus
            ? `Tu plan${nombrePlan ? `: ${nombrePlan}` : ""} · ${precioSus} al mes`
            : `Tu plan${nombrePlan ? `: ${nombrePlan}` : ""}`}
          {/* #1809: la excepción por cuenta, «~~3~~ → 4 jornadas al mes · acordado contigo». Sin excepción, como hoy.
              Dentro del aviso (`modoAviso`) NO: ahí ya la dicen la orden y el «Incluye…» del diálogo (revisor
              jornada v1, F1: salía 3 veces). Fuera del aviso (bloqueo por pago vencido) no hay «Incluye…». */}
          {!modoAviso && hayExcepcionJornadas(sus.jornadas, sus.jornadas_plan) && (
            <Box data-testid="checkout-vencido-jornadas" sx={{ fontSize: 12.5, fontWeight: 400, color: "var(--text-secondary)", mt: 0.5 }}>
              <JornadasAlMes jornadas={sus.jornadas} jornadasPlan={sus.jornadas_plan} />
            </Box>
          )}
        </Box>
        {/* Con la orden a la vista (aviso antes del día de pago) la definición ya va dentro de su texto. */}
        {!(flujoManual && modoAviso && !pagoVencido && orden) && (
          <NotaJornada definicion={estado.definicion_jornada} conTope={sus.plan !== "max_plus" && !(sus.jornadas != null && sinTopeJornadas(sus.jornadas))} />
        )}
        <Box sx={{ mt: 1.75 }}>
          {/* Revisor v2, F4: tras enviar los papeles el pago vencido también confirma que llegaron (el aviso
              manual ya lo pinta dentro de su bloque). */}
          {docsEnRevision && !(flujoManual && modoAviso && !pagoVencido) && <DocumentosEnviados recien={docsEnviados} />}
          {sus.gateway === "paddle" &&
            (sus.portal_disponible ? (
              <BotonActualizarTarjeta />
            ) : (
              mensajeContacto("Escríbenos para actualizar tu tarjeta:")
            ))}
          {sus.gateway === "flow" && mensajeContacto("Escríbenos para actualizar tu tarjeta:")}
          {flujoManual && modoAviso && !pagoVencido && (
            <>
              {docsEnRevision && <DocumentosEnviados recien={docsEnviados} />}
              {orden && <OrdenContratacion orden={orden} acepta={aceptaOrden} onCambio={setAceptaOrden} />}
              <Button
                data-testid="checkout-pagar"
                variant="contained"
                disableElevation
                fullWidth
                disabled={pagando || estado.habilitado === false || faltaOrden}
                onClick={pagar}
                sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14 }}
              >
                {pagando ? "Abriendo Flow..." : etiquetaPagarSus || "Registrar tarjeta con Flow"}
              </Button>
              <Fine>
                Te llevamos a Flow a inscribir tu tarjeta y vuelves aquí. Flow cobra solo el día de pago, IVA incluido.
                Cancelas cuando quieras.
              </Fine>
            </>
          )}
          {flujoManual && !(modoAviso && !pagoVencido) && (
            // I-A (decisión de Sebastián 15-oct): a quien paga por transferencia el primer cobro se
            // le hace por transferencia, y la tarjeta cobra desde el período SIGUIENTE al que pague
            // así. Con el período vencido Flow no lo pone al día (D2 de #862), por eso acá no hay
            // botón de Flow: el camino es la transferencia de abajo. Sin prometer más de lo que hay.
            <Typography data-testid="checkout-vencido-transferencia-primero" sx={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.55 }}>
              Tu período venció. Para volver a usar SmartDx, ponlo al día por transferencia (abajo). Cuando
              registremos tu pago se abre tu acceso. Si quieres dejar tu tarjeta para los períodos siguientes,
              escríbenos a{" "}
              <Box component="a" href={`mailto:${SOPORTE}`} sx={{ color: "var(--color-info)" }}>
                {SOPORTE}
              </Box>
              : el primer cobro con tarjeta es el período siguiente al que pagues por transferencia.
            </Typography>
          )}
          {sus.gateway === "manual" && !flujoManual && mensajeContacto("Escríbenos para renovar tu plan:")}
          {errorPago && (
            <Typography
              data-testid="checkout-error-pago"
              role="alert"
              sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1 }}
            >
              {errorPago}
            </Typography>
          )}
        </Box>
        {typeof abajo === "function" ? abajo(estado, { planElegido: null }) : abajo}
      </>
    );
  } else {
    const noDisponible = riel === "no_disponible";
    const noHabilitado = !noDisponible && estado.habilitado === false;
    // R42: con Paddle apagado la caja de transferencia reemplaza al botón de Paddle.
    const transf = !noDisponible && esPaddle ? estado.transferencia || null : null;
    const soloTransferencia = Boolean(transf) && noHabilitado;
    const montoTr = formatoPrecio(transf?.montos?.[plan] ?? planActual?.precio, "USD");
    const etiquetaPagar = planActual
      ? `Suscribirme con ${RIEL_NOMBRE[riel] || ""}`.trim()
      : "Suscribirme";
    cuerpo = (
      <>
        {noDisponible ? (
          <Box
            data-testid="checkout-no-disponible"
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "rgba(var(--color-warning-rgb), 0.08)",
              border: "1px solid rgba(var(--color-warning-rgb), 0.3)",
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
              Por ahora no tenemos suscripción en tu país
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)", mt: 0.5 }}>
              Escríbenos a{" "}
              <Box component="a" href={`mailto:${SOPORTE}`} sx={{ color: "var(--color-info)" }}>
                {SOPORTE}
              </Box>{" "}
              y vemos cómo ayudarte.
            </Typography>
          </Box>
        ) : (
          <>
            <Box role="radiogroup" aria-label="Plan" sx={{ display: "grid", gap: 1 }}>
              {planes.map((p) => (
                <FilaPlan
                  key={p.plan}
                  plan={p}
                  moneda={estado.moneda}
                  fundador={estado.cohorte === "fundador"}
                  activo={p.plan === plan}
                  onElegir={() => setPlan(p.plan)}
                />
              ))}
            </Box>
            {/* #1887 R33: al final de los planes, solo si el backend dice que esta cuenta puede comprar sueltas. */}
            {ofreceSuelta ? <OpcionSinPlan oferta={oferta} onElegir={() => setModo("suelta")} /> : null}
            <Typography data-testid="checkout-nota-impuestos" sx={{ fontSize: 12, color: "var(--text-muted)", mt: 1.75 }}>
              {notaImpuestos(estado, soloTransferencia)}
            </Typography>
            {/* Exactamente una definición a la vista: con la orden (Flow) va dentro de su texto. */}
            {!orden && <NotaJornada definicion={estado.definicion_jornada} />}
          </>
        )}

        <Box sx={{ mt: 1.5, mb: 1.5 }}>
          <Typography data-testid="checkout-linea-pais" component="span" sx={{ fontSize: 12, color: "var(--text-muted)" }}>
            {lineaPais(estado)}
          </Typography>
          <br />
          <Box
            component="button"
            type="button"
            data-testid="checkout-cambiar-pais"
            onClick={() => setEligiendoPais((v) => !v)}
            sx={enlaceSx}
          >
            Facturas desde otro país? Cambiar
          </Box>
          {eligiendoPais && <SelectorPais actual={estado.pais} onElegir={cambiarPais} />}
        </Box>

        {docsEnRevision && !noDisponible && <DocumentosEnviados recien={docsEnviados} />}
        {!noDisponible && !soloTransferencia && orden && (
          <OrdenContratacion orden={orden} acepta={aceptaOrden} onCambio={setAceptaOrden} />
        )}

        {!noDisponible && esPaddle && (
          <Box
            component="label"
            data-testid="checkout-terminos"
            sx={{
              display: "flex",
              gap: 1.1,
              alignItems: "flex-start",
              fontSize: 12.3,
              lineHeight: 1.5,
              color: "var(--text-tertiary)",
              mb: 1.5,
              p: "10px 12px",
              borderRadius: "12px",
              backgroundColor: "var(--bg-input)",
              border: "1px solid var(--border-soft)",
              cursor: "pointer",
              "& a": { color: "var(--color-info)" },
            }}
          >
            <Box
              component="input"
              type="checkbox"
              data-testid="checkout-terminos-casilla"
              checked={acepta}
              onChange={(e) => setAcepta(e.target.checked)}
              sx={{ mt: "3px", accentColor: "var(--color-primary)", flex: "0 0 auto" }}
            />
            <span>
              Acepto los{" "}
              <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                Términos de uso
              </a>{" "}
              y el{" "}
              <a href="/terminos-internacionales" target="_blank" rel="noopener noreferrer">
                Anexo de tratamiento de datos internacional
              </a>
              . Ver la{" "}
              <a href="https://smardx.com/privacidad.html" target="_blank" rel="noopener noreferrer">
                política de privacidad
              </a>
              .
            </span>
          </Box>
        )}

        {!noDisponible && !soloTransferencia && (
          <>
            <Button
              data-testid="checkout-pagar"
              variant="contained"
              disableElevation
              fullWidth
              disabled={noHabilitado || pagando || !planActual || (esPaddle && !acepta) || faltaOrden}
              onClick={pagar}
              sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14 }}
            >
              {pagando ? `Abriendo ${RIEL_NOMBRE[riel]}...` : etiquetaPagar}
            </Button>
            {errorPago && (
              <Typography
                data-testid="checkout-error-pago"
                role="alert"
                sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1 }}
              >
                {errorPago}
              </Typography>
            )}
            {noHabilitado ? (
              <Fine data-testid="checkout-no-habilitado">{textoNoHabilitado(estado.motivo_no_habilitado)}</Fine>
            ) : (
              <Fine>
                {esPaddle
                  ? "El pago de Paddle se abre aquí mismo, sin salir de SmartDx. Cobro automático cada mes. Cancelas cuando quieras."
                  : "Te llevamos a Flow a inscribir tu tarjeta y vuelves aquí. Cobro automático cada mes, IVA incluido. Cancelas cuando quieras."}
              </Fine>
            )}
          </>
        )}
        {transf && (
          <Box sx={{ mt: soloTransferencia ? 0 : 1.5, display: "grid", gap: 1.25 }}>
            {!soloTransferencia && (
              <Box
                component="button"
                type="button"
                data-testid="checkout-transferencia-abrir"
                aria-expanded={trAbierta}
                onClick={() => setTrAbierta((v) => !v)}
                sx={enlaceSx}
              >
                Prefiero transferencia internacional
              </Box>
            )}
            {(soloTransferencia || trAbierta) && (
              <CajaTransferencia
                transferencia={transf}
                monto={montoTr}
                principal={soloTransferencia}
                deshabilitado={!acepta || !planActual}
                avisando={pagando}
                onAvisar={avisarTransferencia}
              />
            )}
          </Box>
        )}
        {errorPago && (noDisponible || soloTransferencia) && (
          <Typography
            data-testid="checkout-error-pago"
            role="alert"
            sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1.25 }}
          >
            {errorPago}
          </Typography>
        )}
        {/* I-1 (revisor r3): con la lista de planes a la vista, todo monto de `abajo` sale del plan ELEGIDO. */}
        {typeof abajo === "function"
          ? abajo(estado, { planElegido: plan, precio: planActual?.precio, moneda: estado.moneda })
          : abajo}
      </>
    );
  }

  return (
    <Box data-testid="checkout">
      {/* Con la transferencia avisada no hay lista de planes: el título "Elige tu plan" quedaría huérfano. */}
      {paso === 2 && !avisadaEnBloqueo && <PasosPago paso={2} />}
      {paso === 2 && !avisadaEnBloqueo && (
        <Typography data-testid="checkout-titulo-suscripcion" sx={{ fontSize: 19, fontWeight: 800, mb: 1.75, color: "var(--text-primary)", lineHeight: 1.3 }}>
          {sus ? "Tu suscripción" : ofreceSuelta ? "Elige cómo seguir" : "Para continuar, selecciona la suscripción que más te acomode"}
        </Typography>
      )}
      {/* Cubre R33 — .claude/specs/jornada-suelta/ (v7, P23): misma bajada que el caso sin documentos. */}
      {paso === 2 && !avisadaEnBloqueo && !sus && ofreceSuelta && (
        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mt: -1.5, mb: 1.75 }}>Un plan mensual, o solo las jornadas que vas a usar.</Typography>
      )}
      {!avisadaEnBloqueo && titulo && paso === 0 && (
        <>
          {/* #1887: con la suelta a la vista el título no dice «pagar» (Sebastián, 31-oct-2026). */}
          <Typography sx={{ fontSize: 19, fontWeight: 800, mb: ofreceSuelta && titulo === "Elige tu plan" ? 0.25 : 1.75, color: "var(--text-primary)" }}>
            {ofreceSuelta && titulo === "Elige tu plan" ? "Elige cómo seguir" : titulo}
          </Typography>
          {ofreceSuelta && titulo === "Elige tu plan" && (
            <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 1.75 }}>Un plan mensual, o solo las jornadas que vas a usar.</Typography>
          )}
        </>
      )}
      {cuerpo}
      {volver}
    </Box>
  );
};

const enlaceSx = {
  border: 0,
  background: "none",
  p: 0,
  font: "inherit",
  fontSize: 12.2,
  color: "var(--color-info)",
  textDecoration: "underline",
  cursor: "pointer",
  textAlign: "left",
};

export default PlanesYPago;
